import React from 'react';
import { useSelector } from 'react-redux';
import { Route } from 'react-router-dom';
import lazyLoad from 'utils/lazyLoad';
import useFeatureToggle from 'hooks/useFeatureToggle';
import { getPathsForGwtPlace, getRouterPath } from 'utils/navigation';
import { getIsCompanyAdmin } from 'selectors/loggedInUser';
import { isUserAuditReportEnabled } from 'selectors/featureToggles';
import {
  hasViewNotificationReport,
  hasViewAlertsList,
  hasViewMenuReporting,
  hasViewSystemAuditReport,
  hasEditLiveCallRouting,
} from 'selectors/permissions';
import { Redirect } from 'react-router';
import useSignalsReportRoutes from './useSignalsReportRoutes';
import RecentEventsTrackingRedirect from './RecentEventsTrackingRedirect';
import LegacyAlertsRedirect from './LegacyAlertsRedirect/LegacyAlertsRedirect';

const Alerts = lazyLoad(
  () => import(/* webpackChunkName: "Alerts" */ 'pages/Reports/Alerts'),
);
const NotificationReport = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "NotificationReport" */ 'pages/Reports/NotificationReport'
    ),
);
const ConferenceListPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "ConferenceListPage" */ 'pages/Reports/ConferenceListPage'
    ),
);
const AlertViews = lazyLoad(
  () => import(/* webpackChunkName: "AlertViews" */ 'pages/Reports/AlertViews'),
);
const EventDetailsReport = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "EventDetailsReport" */ 'pages/Reports/EventDetailsReport'
    ),
);
const ImportJobDetailsReport = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "ImportJobDetailsReport" */ 'pages/Reports/ImportJobDetailsReport'
    ),
);
const ImportJobHistoryReport = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "ImportJobHistoryReport" */ 'pages/Reports/ImportJobHistoryReport'
    ),
);
const UnauthorizedPageAccess = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "UnauthorizedPageAccess" */ 'pages/UnauthorizedPageAccess'
    ),
);
const WorkflowsAuditReport = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "WorkflowsAuditReport" */ 'pages/Reports/SystemAuditReport'
    ),
);
const SystemAuditReport = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "SystemAuditReport" */ 'pages/Reports/SystemAuditReport'
    ),
);
const LiveCallRoutingReport = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "LiveCallRoutingReport" */ 'pages/Reports/LiveCallRoutingReport'
    ),
);

export default function useReportsRoutes() {
  const isReactNotificationReport = useFeatureToggle(
    'REACT_NOTIFICATIONS_REPORT',
  );
  const isWorkflowAuditsReport = useFeatureToggle('WORKFLOWS_AUDIT_REPORT');
  const isNewSystemAudit = useSelector(isUserAuditReportEnabled);

  const canViewGroupPerformanceReport = useSelector(hasViewNotificationReport);
  const canViewAlertsList = useSelector(hasViewAlertsList);
  const canViewAlertDetails = useSelector(hasViewMenuReporting);
  const canViewSystemAudit = useSelector(hasViewSystemAuditReport);
  const isLiveCallRoutingMVPEnabled = useFeatureToggle('LIVE_CALL_ROUTING_MVP');
  const isCompanyAdmin = useSelector(getIsCompanyAdmin);
  const canViewLiveCallRouting =
    (useSelector(hasEditLiveCallRouting) || isCompanyAdmin) &&
    isLiveCallRoutingMVPEnabled;

  return [
    ...useSignalsReportRoutes(),
    <Route
      path={[
        getRouterPath('/EventsReportPlace:none'),
        getRouterPath('/eventsReport/none'),
      ]}
    >
      <Redirect to={getRouterPath('/HistoricalEventsReport')} />
    </Route>,
    isReactNotificationReport && (
      <Route path={getRouterPath('/CompanyNotificationReportPlace(.+)')}>
        <Redirect to={getRouterPath('/notifications')} />
      </Route>
    ),
    // Each "chiclet" in the original report has its own path, and they all need to redirect to the same place
    isReactNotificationReport && (
      <Route
        path={getRouterPath(
          '/b2BCompanyNotificationReportDrilldown/reportType=(.+),parent=CompanyNotificationReportPlace(.+)',
        )}
      >
        <Redirect to={getRouterPath('/notifications')} />
      </Route>
    ),
    <Route
      path={getRouterPath(
        '/HistoricalEventsReport/:startDate?/:endDate?/:responseOptionsDefined?/:recipientId?/:recipientName?/:recipientType?/:priority?/:workflows?',
      )}
      render={({ location }) =>
        canViewAlertsList ? (
          <LegacyAlertsRedirect pathname={location?.pathname} />
        ) : (
          <UnauthorizedPageAccess />
        )
      }
    />,
    <Route
      path={getRouterPath('/alerts')}
      component={canViewAlertsList ? Alerts : UnauthorizedPageAccess}
    />,
    isReactNotificationReport && (
      <Route
        path={getRouterPath('/notifications')}
        component={
          canViewGroupPerformanceReport
            ? NotificationReport
            : UnauthorizedPageAccess
        }
      />
    ),
    <Route
      path={getRouterPath('/workflowsaudit')}
      component={
        canViewSystemAudit && isWorkflowAuditsReport
          ? WorkflowsAuditReport
          : UnauthorizedPageAccess
      }
    />,
    <Route
      path={getRouterPath('/systemaudit')}
      component={
        canViewSystemAudit && isNewSystemAudit
          ? SystemAuditReport
          : UnauthorizedPageAccess
      }
    />,
    <Route
      path={getRouterPath('/ConferencesReportPlace:none')}
      component={ConferenceListPage}
    />,
    <Route
      path={getRouterPath('/LiveCallRoutingReport')}
      component={
        canViewLiveCallRouting ? LiveCallRoutingReport : UnauthorizedPageAccess
      }
    />,
    <Route
      path={getRouterPath('/AlertViews')}
      component={canViewAlertsList ? AlertViews : UnauthorizedPageAccess}
    />,
    <Route
      path={[
        getRouterPath(
          '/eventDetailsReport(:?)/:eventId/:tab/:filters?/:created?/:formId?/:hasSuppressions?',
        ),
        getRouterPath(
          '/EventDetailsReportPlace:/:eventId/:tab/:filters?/:created?/:formId?/:hasSuppressions?',
        ),
        getRouterPath('/EventDetailsReportPlace:/:eventId/'),
      ]}
      component={
        canViewAlertDetails ? EventDetailsReport : UnauthorizedPageAccess
      }
    />,
    <Route
      path={getRouterPath('/ImportJobDetailsReportPlace:(.+)')}
      component={ImportJobDetailsReport}
    />,
    <Route
      path={getRouterPath('/ImportJobHistoryReportPlace:none')}
      component={ImportJobHistoryReport}
    />,
    <Route
      exact
      path={[
        ...getPathsForGwtPlace('trackingReport', {
          params: 'eventUuid=:eventUuid',
        }),
        ...getPathsForGwtPlace('eventAttachmentsReport', {
          params: 'eventUuid=:eventUuid',
        }),
        ...getPathsForGwtPlace('conferenceBridgeManagement', {
          params: 'eventUuid=:eventUuid',
        }),
        ...getPathsForGwtPlace('eventSuppressedDuplicates', {
          params: 'eventUuid=:eventUuid',
        }),
        ...getPathsForGwtPlace('personDelivery', {
          params: 'eventUuid=:eventUuid,:source?',
        }),
        ...getPathsForGwtPlace('eventReportCard', {
          params: 'eventUuid=:eventUuid',
        }),
        ...getPathsForGwtPlace('eventDetailsMessage', {
          params: 'eventUuid=:eventUuid',
        }),
        ...getPathsForGwtPlace('eventTimeline', {
          params: 'eventUuid=:eventUuid',
        }),
        ...getPathsForGwtPlace('eventLogReport', {
          params: 'eventUuid=:eventUuid',
        }),
        ...getPathsForGwtPlace('eventPropertyReport', {
          params: 'eventUuid=:eventUuid',
        }),
      ]}
    >
      {/**
       * The following <span> is _VERY HACKY_ but fixes a sev-1 related issue.
       * The gist is we determine whether to render GWT pages based on if a React page rendered: if a react page rendered then we know
       * that a given page was converted to React.
       *
       * This is determined by whether the GWT Inject Point is the "first-of-type" in CSS:
       *   `.ApplicationShell-module__pageWrap--RDIdq #content-wrapper > #GWTInjectPoint:not(:first-of-type)`
       *
       * Previously redirectors did not live inside the normal routing area (`#wrap`) but recently they were moved in.
       *
       * As a quick workaround we can use another DOM element type (other than `div`) to ensure that GWTInjectPoint is first-of-type when needed.
       *
       */}
      <span>
        <RecentEventsTrackingRedirect />
      </span>
    </Route>,
  ];
}
